import { ArrowClockwise, WifiSlash } from "@phosphor-icons/react";
import { useConnectionStatus } from "../lib/connection";

/**
 * The reconnect state plan.md section 5.8 asks for, in the one place a client is actually looking.
 *
 * `public/offline.html` exists and is unreachable from inside a session. The service worker serves
 * it on a navigation, and every route change in this application is a `pushState`, so a person who
 * loses their connection while working never navigates and never sees it. The page that says the
 * network is gone is the one page they cannot get to.
 *
 * The reload button is not decoration. On iOS in standalone there is no address bar and no reload
 * control, so a portal that does not carry its own leaves closing and reopening the application as
 * the only way out of a stuck screen.
 *
 * `onReload` is injected so the behaviour can be asserted. jsdom's `location.reload` is not
 * callable, and a test that stubbed it would be asserting on the stub.
 */
export function ConnectionNotice({ onReload }: { onReload?: () => void }) {
  const { online } = useConnectionStatus();

  if (online) {
    return null;
  }

  /*
   * The sketch's words since 2026-09-24 (`prototype/kund/kund.js`, the `offline` overlay): the state
   * in bold, then the two facts a person needs, then the one act. The same three facts as before,
   * in shorter sentences.
   */
  return (
    <div className="k-offline callout tone-warn" role="status" data-testid="connection-notice">
      <WifiSlash size={18} aria-hidden="true" />
      <span>
        <strong>Ingen anslutning.</strong> Det som redan är hämtat går att läsa. Inget skickas förrän
        nätet är tillbaka.
      </span>
      <button
        type="button"
        className="button is-quiet"
        onClick={() => (onReload ? onReload() : window.location.reload())}
      >
        <ArrowClockwise size={16} aria-hidden="true" />
        <span>Ladda om</span>
      </button>
    </div>
  );
}
